import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import useApiRequest from '../../../../lib/hooks/useRequest';
import { Button } from '../../../../ui/components/ui/button';
import Preloader from '../../../../ui/components/ui/preloader';
import { ErrorEntity, Error as ErrorI } from '../../../root/context';
import { BillingTermMetaResponseDto } from '../../../products/types/response';
import { OfferCorrectionMailStatus } from '../../constants/offer-correction-mail-status.enum';
import { toCorrectionFormOptions, toCorrectionStartDateOptions } from '../../lib/correction';
import { canCorrectOffer } from '../../lib/offer-status';
import { CorrectOfferRequestDto, OfferCompanyNameAvailabilityRequestDto } from '../../types/request';
import {
  OfferAcceptanceResponseDto,
  OfferAvailabilityResponseDto,
  OfferCorrectionPreviewResponseDto,
  OfferCorrectionResponseDto,
  OfferFormOptionsResponseDto,
  OfferMetaResponseDto,
  OfferResponseDto
} from '../../types/response';
import OfferCorrectionForm, { OfferCorrectionPreviewResult } from './offer-correction-form';

export interface OfferCorrectionFormWrapperProps {
  offerId: string;
  billingTerms: BillingTermMetaResponseDto[];
  /** Saved, cancelled or not correctable (any more) – back to the offer. */
  onDone: () => void;
  onBack: () => void;
}

type LoadedCorrection =
  | {
      status: 'ready';
      offer: OfferResponseDto;
      acceptance: OfferAcceptanceResponseDto;
      options: OfferFormOptionsResponseDto;
      startDateOptions: string[];
    }
  | { status: 'notCorrectable' }
  | { status: 'notFound' };

// The offer was archived meanwhile – the error modal says so, the offer is shown again.
const STALE_ERRORS = [ErrorEntity.OfferArchivedConflict, ErrorEntity.OfferNotAcceptedConflict];

const MAIL_STATUS_TOAST: Record<OfferCorrectionMailStatus, () => void> = {
  [OfferCorrectionMailStatus.SENT]: () => toast.success('Vertragsdaten wurden gespeichert und die Änderungsmail versendet.'),
  [OfferCorrectionMailStatus.NO_CHANGES]: () =>
    toast.success('Vertragsdaten wurden gespeichert. Die Vertragsmail ändert sich nicht – keine Mail versendet.'),
  [OfferCorrectionMailStatus.FAILED]: () =>
    toast.error('Vertragsdaten wurden gespeichert, aber die Änderungsmail an den Kunden ist fehlgeschlagen. Bitte informiere ihn selbst.')
};

const OfferCorrectionFormWrapper = ({ offerId, billingTerms, onDone, onBack }: OfferCorrectionFormWrapperProps) => {
  const { apiRequest } = useApiRequest();
  const [loaded, setLoaded] = useState<LoadedCorrection | null>(null);

  useEffect(() => {
    const fetchCorrectionData = async () => {
      const [offerRes, optionsRes, metaRes] = await Promise.all([
        apiRequest<OfferResponseDto>(`offers/admin/${offerId}`, 'GET'),
        apiRequest<OfferFormOptionsResponseDto>('offers/admin/form-options', 'GET'),
        apiRequest<OfferMetaResponseDto>('offers/admin/meta', 'GET')
      ]);
      if (!offerRes.data) {
        setLoaded({ status: 'notFound' });
        return;
      }
      if (!optionsRes.data || !metaRes.data) return;

      const offer = offerRes.data;
      if (!canCorrectOffer(offer) || !offer.acceptance) {
        setLoaded({ status: 'notCorrectable' });
        return;
      }
      setLoaded({
        status: 'ready',
        offer,
        acceptance: offer.acceptance,
        options: toCorrectionFormOptions(offer, optionsRes.data),
        startDateOptions: toCorrectionStartDateOptions(metaRes.data.startDateOptions, offer.customer.newCustomerDate)
      });
    };
    fetchCorrectionData();
  }, [apiRequest, offerId]);

  // The organization the offer created keeps its own name – the backend leaves it out for an accepted `excludeOfferId`.
  const checkCompanyNameAvailability = useCallback(
    async (name: string): Promise<boolean | null> => {
      const body: OfferCompanyNameAvailabilityRequestDto = { name, excludeOfferId: offerId };
      const res = await apiRequest<OfferAvailabilityResponseDto>('offers/admin/company-name-validation', 'POST', { body });
      return res.data?.available ?? null;
    },
    [apiRequest, offerId]
  );

  // Without a response (network) there is neither an error nor the error modal.
  const toError = (error: unknown): ErrorI | null => {
    if (!error) toast.error('Es ist ein Fehler aufgetreten');
    const typed = (error as ErrorI | undefined) ?? null;
    if (typed && STALE_ERRORS.includes(typed.entity)) onDone();
    return typed;
  };

  const handlePreview = async (payload: CorrectOfferRequestDto): Promise<OfferCorrectionPreviewResult> => {
    const res = await apiRequest<OfferCorrectionPreviewResponseDto>(`offers/admin/${offerId}/correction-preview`, 'POST', { body: payload });
    return res.data ? { preview: res.data } : { error: toError(res.error) };
  };

  const handleSave = async (payload: CorrectOfferRequestDto): Promise<ErrorI | null> => {
    const res = await apiRequest<OfferCorrectionResponseDto>(`offers/admin/${offerId}`, 'PATCH', { body: payload });
    if (!res.data) return toError(res.error);
    MAIL_STATUS_TOAST[res.data.mailStatus]();
    onDone();
    return null;
  };

  if (!loaded) return <Preloader />;

  if (loaded.status !== 'ready') {
    return (
      <div className="flex flex-col items-start gap-3 rounded-lg bg-white px-9 py-7">
        <p className="text-[16px] text-[#12282A]">
          {loaded.status === 'notFound'
            ? 'Dieses Angebot gibt es nicht (mehr).'
            : 'Nur angenommene, nicht archivierte Angebote haben Vertragsdaten, die korrigiert werden können.'}
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            Zur Übersicht
          </Button>
          {loaded.status === 'notCorrectable' && <Button onClick={onDone}>Ansehen</Button>}
        </div>
      </div>
    );
  }

  return (
    <OfferCorrectionForm
      offer={loaded.offer}
      acceptance={loaded.acceptance}
      billingTerms={billingTerms}
      options={loaded.options}
      startDateOptions={loaded.startDateOptions}
      onCancel={onDone}
      onPreview={handlePreview}
      onSave={handleSave}
      checkCompanyNameAvailability={checkCompanyNameAvailability}
    />
  );
};

export default OfferCorrectionFormWrapper;
